// Global concurrency limiter for outgoing fetch() calls.
//
// WHY THIS EXISTS
// ---------------
// index.js mounts ~49 context providers at the app root, and 47 of them fetch
// on mount. Over HTTP/2 the browser does not queue those — it multiplexes them
// all at once, so a single page load opens ~47 simultaneous connections.
//
// The backend runs on CloudLinux shared hosting with an Entry Processes cap of
// 40 (cPanel -> Resource Usage). Each in-flight request is one PHP process, so a
// single page load exceeded the cap on its own and the server killed the excess
// with "508 Resource Limit Is Reached" — which the browser surfaces as a CORS
// error, because the 508 page is generated above PHP and carries no CORS headers.
//
// This wrapper caps how many requests are in flight at once. Requests over the
// limit queue in memory and start as slots free up; nothing is dropped and no
// calling code changes.
//
// This is a mitigation, not a cure. The real fix is to stop mounting every
// provider globally and let each route fetch only the data it needs.

const MAX_CONCURRENT = 6;

let inFlight = 0;
const queue = [];

const pump = () => {
  if (inFlight >= MAX_CONCURRENT || queue.length === 0) return;

  const job = queue.shift();
  inFlight++;

  // Always release the slot — including on network failure — or the queue
  // would deadlock after the first rejected request.
  job.run().then(job.resolve, job.reject).finally(() => {
    inFlight--;
    pump();
  });
};

export const installFetchLimiter = () => {
  // Guard against double-installation (React 18 StrictMode double-invokes
  // effects in development, and hot reload re-runs module side effects).
  if (typeof window === "undefined" || window.__fetchLimiterInstalled) return;
  window.__fetchLimiterInstalled = true;

  const nativeFetch = window.fetch.bind(window);

  window.fetch = (...args) =>
    new Promise((resolve, reject) => {
      queue.push({ run: () => nativeFetch(...args), resolve, reject });
      pump();
    });
};
